import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import clsx from 'clsx';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import {
    Enrollment,
    EnrollmentStatuses,
    MEMBERSHIP_ITEM_TYPES,
    MembershipItem,
    MembershipItemType
} from '@spike/membership-model';
import { MembershipsStatus, searchEnrollmentsThunk } from '@spike/memberships-action';
import { RootState } from 'store';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { getEnrollmentEndDate, getEnrollmentStartDate } from 'utils/EnrollmentUtils';
import { parseMembershipServiceId } from 'components/CreateBooking/Memberships/utils/membershipItemFlow';
import Button from 'components/UI/V3/Button/Button';
import SelectionBar, { SelectionBarOption } from 'components/UI/SelectionBar/SelectionBar';
import Pagination from 'components/UI/Pagination/Pagination';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';

import styles from './PetMembershipsTab.module.scss';
import { flowFromMembershipItemType } from 'components/CreateBooking/Memberships/utils/membershipItemFlow';

function isServiceItem(item: MembershipItem): boolean {
    if (item.itemType === MembershipItemType.SERVICE || item.itemType === 'Service') return true;
    return (
        item.itemType === MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE
    );
}

function benefitKind(item: MembershipItem): 'included' | 'discount' {
    if ((item.discountPercentage ?? 0) > 0) return 'discount';
    if ((item.discountAmount ?? 0) > 0) return 'discount';
    return 'included';
}

function benefitLabel(item: MembershipItem): string {
    return benefitKind(item) === 'discount' ? 'Discount' : 'Included';
}

function benefitValue(item: MembershipItem): string {
    if ((item.discountPercentage ?? 0) > 0) return `${item.discountPercentage}%`;
    if ((item.discountAmount ?? 0) > 0) return `$${Number(item.discountAmount).toFixed(0)}`;
    if (item.unlimited) return 'Unlimited';
    const info = remainingUses(item);
    if (info && Number.isFinite(info.remaining)) {
        const remaining = Math.max(0, info.remaining);
        if (info.total != null && Number.isFinite(info.total) && info.total > 0) {
            return `${remaining}/${info.total}`;
        }
        if (remaining > 0) return `${remaining}`;
    }
    return item.total ? String(item.total) : '—';
}

function getDaysDiff(timezone: string, endDate: string | Moment | null | undefined): number | null {
    if (endDate == null || !moment(endDate).isValid()) return null;
    const today = moment().tz(timezone).startOf('day');
    const end = moment(endDate).tz(timezone).startOf('day');
    return end.diff(today, 'days');
}

function getDaysLeftText(timezone: string, enrollment: Enrollment): string {
    const endDate = getEnrollmentEndDate(enrollment);
    const daysDiff = getDaysDiff(timezone, endDate);
    if (daysDiff === null) return '-';
    if (daysDiff < 0) return 'Expired';
    if (daysDiff === 0) return 'Expires today';
    return `Expires in ${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`;
}

function isFutureStartDate(timezone: string, enrollment: Enrollment): boolean {
    const start = getEnrollmentStartDate(enrollment);
    if (!start || !moment(start).isValid()) return false;
    return moment().tz(timezone).isBefore(moment(start).tz(timezone), 'minute');
}

function remainingUses(item: MembershipItem): { remaining: number; total?: number } | null {
    if (item.unlimited) return { remaining: Number.POSITIVE_INFINITY };

    if (typeof item.left === 'number') {
        return { remaining: item.left, total: typeof item.quantity === 'number' ? item.quantity : undefined };
    }

    if (typeof item.available === 'number') {
        if (typeof item.consumed === 'number') {
            return { remaining: item.available - item.consumed, total: item.available };
        }
        return { remaining: item.available, total: undefined };
    }

    return null;
}

function isOutOfEnrollmentDateRange(enrollment: Enrollment, selectedDate: Moment): boolean {
    const d = selectedDate.clone().seconds(0).milliseconds(0);
    const start = enrollment.startDate?.clone?.().seconds(0).milliseconds(0);
    const endRaw = enrollment.endDate as unknown;
    const end =
        endRaw && typeof endRaw === 'string'
            ? moment(endRaw).seconds(0).milliseconds(0)
            : (endRaw as Moment | undefined)?.clone?.().seconds(0).milliseconds(0);

    if (start?.isValid() && d.isBefore(start, 'day')) return true;
    if (end?.isValid() && d.isAfter(end, 'day')) return true;
    return false;
}

function isRedeemable(timezone: string, enrollment: Enrollment, item: MembershipItem): boolean {
    if (!isServiceItem(item)) return false;
    if (enrollment.status === EnrollmentStatuses.canceled) return false;
    if (enrollment.status === EnrollmentStatuses.pending) return false;
    if (enrollment.status === EnrollmentStatuses.pastDue) return false;
    if (enrollment.membership?.active === false) return false;

    const selectedDate = moment().tz(timezone);
    if (isOutOfEnrollmentDateRange(enrollment, selectedDate)) return false;

    if (benefitKind(item) === 'discount') return true;

    const info = remainingUses(item);
    if (!info) return true;
    return info.remaining > 0;
}

function getStatusChipClassName(enrollment: Enrollment, future: boolean): string {
    if (future) return styles.statusChipDisabled;
    const s = enrollment.status;
    if (s === EnrollmentStatuses.pending) return styles.statusChipPending;
    if (s === EnrollmentStatuses.pastDue || s === EnrollmentStatuses.canceled || s === EnrollmentStatuses.paused) {
        return styles.statusChipDanger;
    }
    return styles.statusChipActive;
}

type Row = {
    enrollment: Enrollment;
    item: MembershipItem;
    enrollmentId: number;
    serviceId?: number;
    disabled: boolean;
};

type FilterOptionId = 'past_due' | 'active' | 'pending' | 'canceled' | 'all';

interface Props {
    clientId: number;
    petId: number;
    petName: string;
    clientName?: string | null;
}

export const PetMembershipsTab: FunctionComponent<Props> = ({ clientId, petId, petName, clientName }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();

    const membershipsStatus = useSelector<RootState, MembershipsStatus>(s => s.memberships.status);
    const enrollments = useSelector<RootState, Enrollment[]>(s => s.memberships.enrollments);

    const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
    const [filter, setFilter] = useState<FilterOptionId>('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [bookSelection, setBookSelection] = useState<{
        enrollment: Enrollment;
        item: MembershipItem;
        serviceId: number;
    } | null>(null);

    useEffect(() => {
        const statusParam = filter === 'all' ? undefined : filter;
        const key = `${petId}:${filter}`;
        if (loadedForKey === key) return;
        setLoadedForKey(key);
        dispatch(searchEnrollmentsThunk(apiClientWrapper, '', undefined, 1, 100, statusParam, petId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId, filter]);

    const rows = useMemo((): Row[] => {
        const out: Row[] = [];
        for (const enrollment of enrollments || []) {
            if (!enrollment?.pets?.some(p => p.id === petId)) continue;
            if (!enrollment?.pets?.some(p => p.clientId === clientId)) continue;
            const membership = enrollment.membership;
            const items = membership?.membershipItems?.filter(isServiceItem) ?? [];
            for (const item of items) {
                const serviceId = parseMembershipServiceId(item);
                const disabled = !isRedeemable(timezone, enrollment, item) || serviceId == null;
                out.push({
                    enrollment,
                    item,
                    enrollmentId: enrollment.id,
                    serviceId,
                    disabled
                });
            }
        }
        return out;
    }, [enrollments, petId, clientId, timezone]);

    const isLoading =
        membershipsStatus === MembershipsStatus.SearchingEnrollments ||
        membershipsStatus === MembershipsStatus.GettingEnrollments;

    const filterOptions = useMemo((): Array<SelectionBarOption<string>> => {
        const withPastDueIcon = (
            <>
                <FontAwesomeIcon
                    style={{ position: 'relative', left: -5, color: '#E36E74' }}
                    icon={faExclamationCircle}
                />{' '}
                Past Due
            </>
        );

        return [
            { id: 'past_due', name: withPastDueIcon, selectedBackgroundColor: '#E36E74' },
            { id: 'active', name: 'Active' },
            { id: 'pending', name: 'Pending' },
            { id: 'canceled', name: 'Canceled' },
            { id: 'all', name: 'All' }
        ];
    }, []);

    const filteredRows = useMemo(() => {
        if (filter === 'all') return rows;
        return rows.filter(r => String(r.enrollment.status) === filter);
    }, [rows, filter]);

    const totalCount = filteredRows.length;
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
    const safePage = Math.min(page, totalPages);
    const pageRows = useMemo(() => {
        if (pageSize === 0) return filteredRows;
        const start = (safePage - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, pageSize, safePage]);

    return (
        <>
            <Box className={styles.container}>
                <Box className={styles.headerWrap}>
                    <Typography className={styles.title}>Memberships</Typography>

                    <Box className={styles.filtersRow}>
                        <SelectionBar
                            options={filterOptions}
                            selectedId={filter}
                            selectedTotalRecords={totalCount}
                            onSelect={id => {
                                setFilter(id as FilterOptionId);
                                setPage(1);
                            }}
                            className={styles.selectionBar}
                        />
                    </Box>
                </Box>

                {isLoading ? (
                    <Box
                        className={styles.loadingWrap}
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <Spinner size={48} />
                    </Box>
                ) : (
                    <>
                        <Table
                            className={styles.table}
                            aria-label="Pet memberships"
                        >
                            <TableHead>
                                <TableRow className={styles.headerRow}>
                                    <TableCell className={styles.headerCell}>Item / Service</TableCell>
                                    <TableCell className={styles.headerCell}>Membership</TableCell>
                                    <TableCell className={styles.headerCell}>Benefit</TableCell>
                                    <TableCell className={styles.headerCell}>Days Left</TableCell>
                                    <TableCell className={styles.headerCell}>Status</TableCell>
                                    <TableCell className={clsx(styles.headerCell, styles.actionsCell)}></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography>No memberships found for this pet.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {pageRows.map((row, idx) => {
                                    const membership = row.enrollment.membership;
                                    const membershipType =
                                        membership?.enrollmentType === 'package' ? 'Package' : 'Subscription';
                                    const daysLeftText = getDaysLeftText(timezone, row.enrollment);
                                    const daysDiff = getDaysDiff(timezone, getEnrollmentEndDate(row.enrollment));
                                    const future = isFutureStartDate(timezone, row.enrollment);

                                    const statusLabel = future ? 'Future Start' : row.enrollment.status;
                                    const chipClass = getStatusChipClassName(row.enrollment, future);

                                    return (
                                        <TableRow
                                            key={`${row.enrollmentId}-${row.item.id ?? row.item.itemID ?? idx}`}
                                            className={row.disabled ? styles.rowDisabled : undefined}
                                        >
                                            <TableCell>
                                                <Typography>{row.item.itemName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{membership?.name}</Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {membershipType}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography className={styles.benefitInline}>
                                                    {benefitLabel(row.item)} {benefitValue(row.item)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    className={
                                                        daysDiff != null && daysDiff >= 0 && daysDiff <= 10
                                                            ? styles.daysLeftWarning
                                                            : undefined
                                                    }
                                                >
                                                    {daysLeftText}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    className={chipClass}
                                                    label={String(statusLabel)}
                                                />
                                            </TableCell>
                                            <TableCell className={styles.actionsCell}>
                                                <Button
                                                    label="Book Now"
                                                    onClick={() =>
                                                        setBookSelection({
                                                            enrollment: row.enrollment,
                                                            item: row.item,
                                                            serviceId: row.serviceId!
                                                        })
                                                    }
                                                    disabled={row.disabled || row.serviceId == null}
                                                    id={`pet_membership_book_${row.enrollmentId}_${
                                                        row.serviceId ?? 'na'
                                                    }`}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {totalCount > 0 && (
                            <Box className={styles.paginationRow}>
                                <Pagination
                                    count={totalCount}
                                    page={safePage}
                                    pageSize={pageSize}
                                    onPageChange={setPage}
                                    onPageSizeChange={size => {
                                        setPageSize(size);
                                        setPage(1);
                                    }}
                                    className={styles.pagination}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
            {bookSelection && (
                <CreateBookingDrawer
                    fromClient={true}
                    parentID={clientId}
                    petID={petId}
                    flow={(flowFromMembershipItemType(bookSelection.item.itemType) ?? 'grooming') as any}
                    initialMembershipSelection={{
                        membership: bookSelection.enrollment.membership,
                        item: bookSelection.item,
                        enrollmentId: bookSelection.enrollment.id
                    }}
                    initialMembershipServiceId={bookSelection.serviceId}
                    onClose={() => setBookSelection(null)}
                    onBooked={() => setBookSelection(null)}
                />
            )}
        </>
    );
};

export default PetMembershipsTab;
